import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Mail } from "lucide-react";
import { Reveal, Label } from "../components/site/Reveal";
import { SITE } from "../config/site";

const topics = [
  {
    id: "contact",
    title: "Contact support",
    body: `The fastest way to reach us is by email at ${SITE.supportEmail}. Include the email address on your account, your device model, and your app version (found in the app under Settings), and we'll get back to you — typically within 1–2 business days.`,
  },
  {
    id: "account-access",
    title: "Logging in / account access",
    body: "Sign in with the email address you used when creating your NXTPlate account. If you've forgotten your password, use the password reset option on the sign-in screen. If you no longer have access to that email address, contact support and we'll help verify and recover your account.",
  },
  {
    id: "food-logging",
    title: "Food logging",
    body: "You can log meals by searching the food database or picking from your recent and frequent foods. Any entry can be edited or deleted from your daily log — tap the entry, adjust portions or details, and save. Your daily totals and remaining nutrition update automatically.",
  },
  {
    id: "photo-logging",
    title: "Photo logging",
    body: "Snap a photo of your plate and NXTPlate estimates the meal's calories and macros for you to review. For the best results: use good lighting, keep the whole plate in frame, and photograph before you start eating. Always review the estimate before saving — you can adjust portions and items at any time.",
  },
  {
    id: "subscriptions",
    title: "Subscription questions",
    body: "NXTPlate offers free access, NXTPlate Pro, and NXTPlate AI Coach. Paid plans are billed through your Apple ID as an in-app purchase, and pricing is confirmed in the app before you subscribe. For anything billing-related — charges, receipts, or plan changes — email us and we'll help sort it out.",
  },
  {
    id: "restore-purchases",
    title: "Restore purchases",
    body: "Reinstalled the app or switched to a new iPhone? Open NXTPlate, go to Settings, and tap Restore Purchases while signed in with the same Apple ID you used to subscribe. Your Pro or AI Coach access will be reinstated. If it doesn't appear after restoring, contact support.",
  },
  {
    id: "apple-subscription",
    title: "Manage or cancel an Apple subscription",
    body: "Subscriptions are managed by Apple through your Apple ID. To change or cancel: open the Settings app on your iPhone, tap your name at the top, tap Subscriptions, select NXTPlate, and choose to switch plans or cancel. You can also manage subscriptions at reportaproblem.apple.com. Cancellation stops the next renewal — you keep access until the end of the current billing period. Refunds are handled by Apple under its own policies.",
  },
  {
    id: "account-deletion",
    title: "Account deletion",
    body: "You can delete your account at any time: open NXTPlate, go to Settings, then Account, then Delete Account, and confirm. You can also request deletion by emailing support from the address on your account. Deleting your account permanently removes your profile, food logs, photos, and wellness data in line with our Privacy Policy. Note that deleting your account does not cancel an active Apple subscription — cancel it first via your Apple ID settings.",
  },
];

const AccordionItem = ({ topic, open, onToggle }) => (
  <div id={topic.id} data-testid={`support-topic-${topic.id}`} className="border-b border-neutral-200">
    <button
      data-testid={`support-topic-${topic.id}-toggle`}
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center justify-between gap-6 py-6 text-left"
    >
      <span className="font-display text-xl font-medium tracking-tight text-neutral-900 md:text-2xl">
        {topic.title}
      </span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${open ? "bg-coral text-white" : "bg-bone text-neutral-600"}`}
      >
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="max-w-3xl pb-8 text-base leading-relaxed text-neutral-600">{topic.body}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Support() {
  const [openId, setOpenId] = useState("contact");
  return (
    <main className="bg-cream">
      <section className="noise relative overflow-hidden pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <Reveal><Label>Support</Label></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl font-medium tracking-tight text-neutral-900 md:text-7xl">
              NXTPlate <span className="italic text-coral">Support</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-600 md:text-lg">
              Need help with NXTPlate? We're here to help.
            </p>
          </Reveal>

          <Reveal delay={0.24}>
            <div
              data-testid="support-contact-card"
              className="mt-12 flex flex-col items-start gap-6 rounded-3xl border border-neutral-200/70 bg-white p-8 shadow-[0_8px_24px_rgba(0,0,0,0.06)] sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Email us anytime</p>
                <a
                  href={`mailto:${SITE.supportEmail}`}
                  data-testid="support-email-link"
                  className="mt-2 inline-flex items-center gap-3 font-display text-2xl font-medium tracking-tight text-neutral-900 transition-colors duration-200 hover:text-coral md:text-3xl"
                >
                  <Mail className="h-6 w-6 text-coral" aria-hidden="true" />
                  {SITE.supportEmail}
                </a>
              </div>
              <p className="max-w-xs text-sm leading-relaxed text-neutral-500">
                Typical reply within 1–2 business days. Include your account email and app version for a faster answer.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="support-topics-section" className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Reveal><Label>How can we help?</Label></Reveal>
          <div className="mt-8 border-t border-neutral-200">
            {topics.map((t) => (
              <AccordionItem
                key={t.id}
                topic={t}
                open={openId === t.id}
                onToggle={() => setOpenId(openId === t.id ? null : t.id)}
              />
            ))}
          </div>
          <Reveal className="mt-12">
            <p className="text-sm leading-relaxed text-neutral-500">
              NXTPlate is a wellness and nutrition app and does not provide medical advice, diagnosis,
              or treatment. For medical questions, please consult a qualified healthcare professional.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
